const section = document.querySelector(".cinema-scroll");
const root = document.documentElement;
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const sightsTrack = document.querySelector(".sights-track");
const sightsControls = document.querySelector(".sights-controls");
const sightPrev = document.querySelector(".sight-prev");
const sightNext = document.querySelector(".sight-next");
const originalSightCards = Array.from(document.querySelectorAll(".sight-card"));

let targetMouseX = 0;
let targetMouseY = 0;
let mouseX = 0;
let mouseY = 0;
let targetScroll = 0;
let smoothScroll = 0;
let initialized = false;
let rafPending = false;
let sightCards = [];
const originalSightCount = originalSightCards.length;
let activeSight = originalSightCount;

const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));

const smoothstep = (e0, e1, v) => {
  const x = clamp((v - e0) / (e1 - e0));
  return x * x * (3 - 2 * x);
};

const lerp = (a, b, t) => a + (b - a) * t;

const segmentInOut = (s, a, b, c, d) => {
  const enter = smoothstep(a, b, s);
  const exit = smoothstep(c, d, s);
  return { enter, exit, active: enter * (1 - exit) };
};

const getScrollDistance = () =>
  clamp(-section.getBoundingClientRect().top, 0, section.offsetHeight - window.innerHeight);

/* ---------- infinite product slider ---------- */

function setupSightSlider() {
  if (!sightsTrack || !originalSightCount) return;

  const clones = [];
  for (let setIndex = 0; setIndex < 3; setIndex += 1) {
    originalSightCards.forEach((card, cardIndex) => {
      const clone = card.cloneNode(true);
      clone.dataset.sightIndex = String(setIndex * originalSightCount + cardIndex);
      clones.push(clone);
    });
  }
  sightsTrack.replaceChildren(...clones);

  sightCards = Array.from(sightsTrack.querySelectorAll(".sight-card"));
  activeSight = originalSightCount;

  sightCards.forEach((card) => {
    card.addEventListener("click", () => selectSightCard(card));
    card.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        selectSightCard(card);
      }
    });
  });

  sightsTrack.addEventListener("transitionend", normalizeSightSlider);
  updateSightSlider();
}

function updateSightSlider() {
  if (!sightsTrack || !sightCards.length) return;
  const cardWidth = sightCards[0].offsetWidth;
  const gap = parseFloat(getComputedStyle(sightsTrack).columnGap || "0") || 0;
  root.style.setProperty("--sights-shift", `${-(cardWidth + gap) * activeSight}px`);
  sightCards.forEach((card) => {
    card.classList.toggle("is-active", Number(card.dataset.sightIndex) === activeSight);
  });
}

function moveSightSlider(direction) {
  activeSight += direction;
  updateSightSlider();
}

function selectSightCard(card) {
  const index = Number(card.dataset.sightIndex);
  if (Number.isFinite(index)) activeSight = index;
  updateSightSlider();
}

function jumpSightSlider(index) {
  sightsTrack.classList.add("is-jumping");
  activeSight = index;
  updateSightSlider();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => sightsTrack.classList.remove("is-jumping"));
  });
}

function normalizeSightSlider() {
  if (activeSight >= originalSightCount * 2) {
    jumpSightSlider(activeSight - originalSightCount);
  } else if (activeSight < originalSightCount) {
    jumpSightSlider(activeSight + originalSightCount);
  }
}

/* ---------- per-frame update ---------- */

function update() {
  rafPending = false;

  targetScroll = getScrollDistance();
  if (!initialized || reduceMotion.matches) {
    smoothScroll = targetScroll;
    initialized = true;
  } else {
    smoothScroll = lerp(smoothScroll, targetScroll, 0.14);
  }
  if (Math.abs(smoothScroll - targetScroll) < 0.08) smoothScroll = targetScroll;

  mouseX = lerp(mouseX, targetMouseX, 0.12);
  mouseY = lerp(mouseY, targetMouseY, 0.12);

  const frame2 = segmentInOut(smoothScroll, 560, 900, 1300, 1620);
  const frame3 = segmentInOut(smoothScroll, 1760, 2140, 2540, 2700);
  const progress = clamp(smoothScroll / 2700);
  const introExit = smoothstep(90, 650, smoothScroll);
  const sightsEnterRaw = smoothstep(2760, 3560, smoothScroll);
  const sightsEnter = Math.pow(sightsEnterRaw, 1.55);
  const sightsControlsEnter = smoothstep(3360, 3660, smoothScroll);
  const blurActive = clamp(frame2.active + frame3.active);
  const frame2Opacity = frame2.active * (1 - frame3.enter);
  const splitDrift = Math.pow(frame2.enter, 1.5);
  const panel2Opacity = frame2.active * (1 - frame2.exit);
  const panel3Opacity = frame3.active * (1 - frame3.exit);
  const backScale = 0.76 + progress * 0.2 + frame2.enter * 0.18 + frame3.enter * 0.16;
  const sharedHeroY = progress * -74;
  const sharedHeroScale = progress * 0.23;
  const sightsScreenTop = Math.min(220, Math.max(112, window.innerHeight * 0.19)) - 50;
  const sightsParentTop = window.innerHeight - (window.innerHeight - sightsScreenTop) / backScale;

  root.style.setProperty("--mx", (reduceMotion.matches ? 0 : mouseX).toFixed(4));
  root.style.setProperty("--my", (reduceMotion.matches ? 0 : mouseY).toFixed(4));

  root.style.setProperty("--back-opacity", (1 - frame2.active * 0.06).toFixed(4));
  root.style.setProperty("--back-x", `${(mouseX * -12).toFixed(2)}px`);
  root.style.setProperty("--back-y", `${(mouseY * -4).toFixed(2)}px`);
  root.style.setProperty("--back-scale", backScale.toFixed(4));
  root.style.setProperty("--four-y", `${(10 + progress * 10).toFixed(3)}vh`);
  root.style.setProperty("--four-scale", (0.78 + progress * 0.16).toFixed(4));
  root.style.setProperty("--bazaar-y", `${(20 - progress * 8).toFixed(3)}vh`);
  root.style.setProperty("--blur-px", `${(blurActive * 14).toFixed(2)}px`);
  root.style.setProperty("--back-brightness", (1 - blurActive * 0.255).toFixed(4));
  root.style.setProperty("--bazaar-blur-px", `${(frame2.active * 14).toFixed(2)}px`);
  root.style.setProperty("--bazaar-brightness", (1 - frame2.active * 0.255 - frame3.active * 0.06).toFixed(4));
  root.style.setProperty("--bazaar-saturation", (1 + frame3.active * 0.18).toFixed(4));
  root.style.setProperty("--shade-opacity", "1");
  root.style.setProperty("--shade-z", frame2.active > 0.02 ? "2" : "0");
  root.style.setProperty("--shade-top-alpha", (blurActive * 0.465).toFixed(4));
  root.style.setProperty("--shade-mid-alpha", (blurActive * 0.42).toFixed(4));
  root.style.setProperty("--shade-bottom-alpha", (blurActive * 0.51).toFixed(4));

  root.style.setProperty("--title-y", `${(introExit * -210).toFixed(2)}px`);
  root.style.setProperty("--title-scale", (1 - introExit * 0.08).toFixed(4));
  root.style.setProperty("--title-opacity", (1 - introExit).toFixed(4));

  root.style.setProperty("--bridge-x", `calc(-50% + ${(mouseX * 18).toFixed(2)}px)`);
  root.style.setProperty("--bridge-y", `${(mouseY * 8 + sharedHeroY - frame2.exit * 760).toFixed(2)}px`);
  root.style.setProperty("--bridge-bottom", `${(5 - frame2.enter * 13).toFixed(3)}vh`);
  root.style.setProperty("--bridge-width", `${(67.2 + frame2.enter * 37.8).toFixed(3)}vw`);
  root.style.setProperty("--bridge-scale", (1.02 + sharedHeroScale + frame2.exit * 0.46).toFixed(4));

  root.style.setProperty("--split-left-x", `calc(-50% + ${(-splitDrift * 46).toFixed(3)}vw + ${(mouseX * 22).toFixed(2)}px)`);
  root.style.setProperty("--split-left-y", `${(mouseY * 10 + sharedHeroY - splitDrift * 180).toFixed(2)}px`);
  root.style.setProperty("--split-left-scale", (1 + sharedHeroScale + frame2.enter * 0.74).toFixed(4));
  root.style.setProperty("--split-right-x", `calc(-50% + ${(splitDrift * 46).toFixed(3)}vw + ${(mouseX * 22).toFixed(2)}px)`);
  root.style.setProperty("--split-right-y", `${(mouseY * 10 + sharedHeroY - splitDrift * 180).toFixed(2)}px`);
  root.style.setProperty("--split-right-scale", (1 + sharedHeroScale + frame2.enter * 0.74).toFixed(4));

  root.style.setProperty("--frame2-opacity", frame2Opacity.toFixed(4));
  root.style.setProperty("--frame2-x", `calc(-50% + ${(mouseX * 10).toFixed(2)}px)`);
  root.style.setProperty("--frame2-y", `calc(-50% + ${(mouseY * 8 - frame2.exit * 150).toFixed(2)}px)`);
  root.style.setProperty("--frame2-scale", (1.06 + frame2.enter * 0.08 + frame2.exit * 0.08).toFixed(4));

  root.style.setProperty("--intro-copy-y", `${(introExit * 90).toFixed(2)}px`);
  root.style.setProperty("--intro-copy-opacity", (1 - introExit).toFixed(4));
  root.style.setProperty("--panel2-opacity", panel2Opacity.toFixed(4));
  root.style.setProperty("--panel2-y", `calc(-50% + ${(-frame2.exit * 86 + (1 - frame2.enter) * 58).toFixed(2)}px)`);
  root.style.setProperty("--panel3-opacity", panel3Opacity.toFixed(4));
  root.style.setProperty("--panel3-y", `calc(-50% + ${(-frame3.exit * 86 + (1 - frame3.enter) * 58).toFixed(2)}px)`);

  root.style.setProperty("--sights-opacity", sightsEnter.toFixed(4));
  root.style.setProperty("--sights-controls-opacity", sightsControlsEnter.toFixed(4));
  if (sightsControls) sightsControls.classList.toggle("is-ready", sightsControlsEnter > 0.98);
  root.style.setProperty("--sights-visibility", sightsEnter > 0.01 ? "visible" : "hidden");
  root.style.setProperty("--sights-y", "0px");
  root.style.setProperty("--sights-enter-x", `${((1 - sightsEnter) * 420).toFixed(3)}vw`);
  root.style.setProperty("--sights-scale", (1 / backScale).toFixed(4));
  root.style.setProperty("--sights-top", `${sightsParentTop.toFixed(2)}px`);
  root.style.setProperty("--sights-screen-top", `${sightsScreenTop.toFixed(2)}px`);

  if (
    Math.abs(smoothScroll - targetScroll) > 0.08 ||
    Math.abs(mouseX - targetMouseX) > 0.001 ||
    Math.abs(mouseY - targetMouseY) > 0.001
  ) {
    requestTick();
  }
}

function requestTick() {
  if (rafPending) return;
  rafPending = true;
  requestAnimationFrame(update);
}

window.addEventListener("scroll", requestTick, { passive: true });

window.addEventListener("resize", () => {
  updateSightSlider();
  requestTick();
});

window.addEventListener("pointermove", (event) => {
  targetMouseX = event.clientX / window.innerWidth - 0.5;
  targetMouseY = event.clientY / window.innerHeight - 0.5;
  requestTick();
}, { passive: true });

if (sightPrev) sightPrev.addEventListener("click", () => moveSightSlider(-1));
if (sightNext) sightNext.addEventListener("click", () => moveSightSlider(1));

setupSightSlider();
requestTick();

const marqueeTrack = document.querySelector(".marquee-track");
const galleryTrack = document.querySelector(".gallery-track");

function trackProgress(el) {
  const rect = el.getBoundingClientRect();
  const span = window.innerHeight + rect.height;
  return clamp((window.innerHeight - rect.top) / span);
}

function updateStrips() {
  if (marqueeTrack) {
    const travel = marqueeTrack.scrollWidth / 2;
    const p = trackProgress(marqueeTrack.parentElement);
    root.style.setProperty("--marquee-x", (-travel * p).toFixed(1) + "px");
  }
  if (galleryTrack) {
    const travel = galleryTrack.scrollWidth - galleryTrack.parentElement.offsetWidth;
    const p = trackProgress(galleryTrack.parentElement);
    root.style.setProperty("--gallery-x", (-Math.max(travel, 0) * 0.55 * p).toFixed(1) + "px");
  }
}

let stripsPending = false;
function requestStrips() {
  if (stripsPending) return;
  stripsPending = true;
  requestAnimationFrame(() => { stripsPending = false; updateStrips(); });
}
window.addEventListener("scroll", requestStrips, { passive: true });
window.addEventListener("resize", requestStrips);
window.addEventListener("load", updateStrips);
updateStrips();

const revealTargets = document.querySelectorAll(
  ".features li, .shop .section-head, .buy-card, .shop-foot, .about .section-head, .stats > div, .pillars article, .team-card, .site-foot"
);
if ("IntersectionObserver" in window) {
  revealTargets.forEach((el, i) => {
    el.classList.add("reveal");
    el.style.transitionDelay = (i % 6) * 70 + "ms";
  });
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add("is-in");
      revealObserver.unobserve(entry.target);
    });
  }, { rootMargin: "0px 0px -12% 0px", threshold: 0.12 });
  revealTargets.forEach((el) => revealObserver.observe(el));
}


/* -------------------------------------------------------------- catalogue */

const inr = (rupees) => "₹" + rupees.toLocaleString("en-IN");

// Display data only — Shopify Checkout re-prices every line from its variant id.
const catalogue = new Map(
  JSON.parse(document.getElementById("product-data").textContent).map((product) => [product.id, product])
);

const MAX_QTY = 10;

const lineTotal = (line) => catalogue.get(line.id).price * line.qty;
const linesTotal = (lines) => lines.reduce((sum, line) => sum + lineTotal(line), 0);

function stepper(line, onChange) {
  const wrap = document.createElement("div");
  wrap.className = "checkout-qty";
  for (const step of [-1, 1]) {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = step < 0 ? "−" : "+";
    button.setAttribute("aria-label", step < 0 ? "Decrease quantity" : "Increase quantity");
    button.addEventListener("click", () => onChange(line.qty + step));
    if (step < 0) {
      wrap.append(button);
      const count = document.createElement("span");
      count.textContent = line.qty;
      count.setAttribute("aria-live", "polite");
      wrap.append(count);
    } else {
      wrap.append(button);
    }
  }
  return wrap;
}

function lineRow(line, className, onChange, onRemove) {
  const product = catalogue.get(line.id);
  const row = document.createElement("div");
  row.className = className;

  const image = document.createElement("img");
  image.src = product.images[0];
  image.alt = product.title;
  image.loading = "lazy";

  const body = document.createElement("div");
  body.className = className === "cart-line" ? "cart-line-body" : "checkout-summary-body";
  const title = document.createElement("h3");
  title.textContent = product.title;
  const unit = document.createElement("p");
  unit.textContent = inr(product.price) + " each";
  body.append(title, unit, stepper(line, onChange));

  const end = document.createElement("div");
  end.className = className === "cart-line" ? "cart-line-end" : "checkout-total";
  if (className === "cart-line") {
    const total = document.createElement("div");
    total.className = "cart-line-total";
    total.textContent = inr(lineTotal(line));
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "cart-remove";
    remove.textContent = "Remove";
    remove.addEventListener("click", onRemove);
    end.append(total, remove);
  } else {
    end.textContent = inr(lineTotal(line));
  }

  row.append(image, body, end);
  return row;
}

/* ------------------------------------------------------------ product view */

const productModal = document.getElementById("product-modal");
const productStage = document.getElementById("product-stage");
const productImage = document.getElementById("product-image");
const productThumbs = document.getElementById("product-thumbs");

// Two products are shot on white and need a matching tile instead of the dark one.
const lightShots = new Set(
  [...document.querySelectorAll(".buy-card")]
    .filter((card) => card.querySelector(".buy-media-light"))
    .map((card) => card.querySelector(".buy-now").dataset.product)
);

let viewed = null;
let productLastFocused = null;

function showImage(index) {
  productImage.src = viewed.images[index];
  productThumbs.querySelectorAll("button").forEach((thumb, i) => {
    thumb.classList.toggle("is-active", i === index);
  });
}

function openProduct(id) {
  viewed = catalogue.get(id);
  if (!viewed) return;

  productLastFocused = document.activeElement;
  document.getElementById("product-title").textContent = viewed.title;
  document.getElementById("product-copy").textContent = viewed.description;
  document.getElementById("product-price").textContent = inr(viewed.price);
  document.getElementById("product-mrp").textContent = inr(viewed.mrp);
  document.getElementById("product-save").textContent =
    "Save " + Math.round(((viewed.mrp - viewed.price) / viewed.mrp) * 100) + "%";

  productStage.classList.toggle("is-light", lightShots.has(id));
  productImage.alt = viewed.title;

  productThumbs.replaceChildren(
    ...viewed.images.map((src, i) => {
      const thumb = document.createElement("button");
      thumb.type = "button";
      thumb.setAttribute("aria-label", `View image ${i + 1}`);
      const img = document.createElement("img");
      img.src = src;
      img.alt = "";
      img.loading = "lazy";
      thumb.append(img);
      thumb.addEventListener("click", () => showImage(i));
      return thumb;
    })
  );
  productThumbs.hidden = viewed.images.length < 2;
  showImage(0);

  productModal.hidden = false;
  productModal.classList.add("is-open");
  document.body.classList.add("checkout-open");
  productModal.querySelector(".checkout-close").focus();
}

function closeProduct(restoreFocus = true) {
  productModal.classList.remove("is-open");
  productModal.hidden = true;
  document.body.classList.remove("checkout-open");
  if (restoreFocus && productLastFocused) productLastFocused.focus();
}

document.querySelectorAll("[data-view]").forEach((button) => {
  button.addEventListener("click", () => openProduct(button.dataset.view));
});

document.querySelectorAll("[data-product-close]").forEach((button) => {
  button.addEventListener("click", () => closeProduct());
});

productModal.addEventListener("click", (event) => {
  if (event.target === productModal) closeProduct();
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && productModal.classList.contains("is-open")) closeProduct();
});


document.getElementById("product-buy").addEventListener("click", () => buyNow(viewed.id));

document.getElementById("product-add").addEventListener("click", () => addToCart(viewed.id));

document.querySelectorAll(".buy-now").forEach((button) => {
  button.addEventListener("click", () => buyNow(button.dataset.product));
});

/* ---------------------------------------------------------------------- cart */
// The cart lives in Shopify, not here. This module is a view over /cart.js: it
// writes optimistically so the steppers feel instant, then reconciles against
// whatever Shopify actually says.

const cartModal = document.getElementById("cart-modal");
const cartFab = document.getElementById("cart-fab");
const cartCount = document.getElementById("cart-count");
const cartLinesEl = document.getElementById("cart-lines");
const cartEmpty = document.getElementById("cart-empty");
const cartFoot = document.getElementById("cart-foot");
const cartTotal = document.getElementById("cart-total");
const cartCheckout = document.getElementById("cart-checkout");
const cartToast = document.getElementById("cart-toast");

// Localised storefronts prefix every cart route, so never hardcode "/cart.js".
const routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

// Shopify speaks variant ids; the rest of this file speaks catalogue ids.
const variantOf = new Map([...catalogue].map(([id, product]) => [id, String(product.variantId)]));
const idOfVariant = new Map([...variantOf].map(([id, variantId]) => [variantId, id]));

const cart = new Map();
let cartLastFocused = null;
let toastTimer = null;

const cartLines = () => [...cart].map(([id, qty]) => ({ id, qty }));

// Writes are queued rather than fired in parallel: a burst of stepper clicks
// would otherwise land out of order and leave Shopify holding an older count.
let cartWrites = Promise.resolve();
let pendingWrites = 0;

async function postCart(path, payload) {
  const response = await fetch(routeRoot + path, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    // A rejected write is usually stock running out mid-session, and Shopify's
    // own wording ("You can only add 2 of this to your cart") is more useful
    // to the buyer than anything generic this file could invent.
    const reason = (data && (data.description || data.message)) || "Cart write failed";
    throw new Error(reason);
  }
  return data;
}

function queueWrite(updates) {
  pendingWrites += 1;
  cartWrites = cartWrites
    .catch(() => {})
    .then(async () => {
      try {
        const data = await postCart("cart/update.js", { updates });
        // Only the last write of a burst may redraw — an earlier response
        // carries a stale count and would visibly rewind the stepper.
        if (pendingWrites === 1) syncFromShopify(data);
      } catch (error) {
        toast(error.message || "Cart didn't save — check your connection.");
        try {
          syncFromShopify(await (await fetch(routeRoot + "cart.js")).json());
        } catch {
          /* offline; the optimistic view is the best we have */
        }
      } finally {
        pendingWrites -= 1;
      }
    });
  return cartWrites;
}

// Shopify's cart can hold products this page doesn't sell — a leftover session,
// or something added from a product URL. Those lines stay in the cart and in
// checkout; they simply can't be drawn here, so they are skipped, not deleted.
function syncFromShopify(data) {
  cart.clear();
  for (const item of data.items || []) {
    const id = idOfVariant.get(String(item.variant_id));
    if (id) cart.set(id, Math.min(MAX_QTY, item.quantity));
  }
  renderCart();
}

function setCartQty(id, qty) {
  qty = Math.max(0, Math.min(MAX_QTY, qty));
  if (qty < 1) cart.delete(id);
  else cart.set(id, qty);
  renderCart();
  queueWrite({ [variantOf.get(id)]: qty });
}

function toast(message) {
  cartToast.textContent = message;
  cartToast.classList.add("is-open");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => cartToast.classList.remove("is-open"), 2200);
}

function addToCart(id) {
  if (!catalogue.has(id)) return;
  const current = cart.get(id) || 0;
  const wasFull = current >= MAX_QTY;
  if (!wasFull) setCartQty(id, current + 1);

  cartFab.classList.remove("is-bumped");
  void cartFab.offsetWidth;
  cartFab.classList.add("is-bumped");
  toast(wasFull ? `Max ${MAX_QTY} per order` : `${catalogue.get(id).title} added`);
}

// Shopify's own buy-it-now permalink: it builds a single-line cart and goes
// straight to checkout, so the buyer is never billed for the rest of the cart.
function buyNow(id) {
  const variantId = variantOf.get(id);
  if (!variantId) return;
  closeProduct(false);
  location.href = `${routeRoot}cart/${variantId}:1`;
}

function renderCart() {
  const lines = cartLines();
  const count = lines.reduce((sum, line) => sum + line.qty, 0);

  cartCount.textContent = count;
  cartFab.classList.toggle("is-hidden", count === 0);
  cartFab.setAttribute("aria-label", count === 1 ? "Open cart, 1 item" : `Open cart, ${count} items`);

  cartLinesEl.replaceChildren(
    ...lines.map((line) =>
      lineRow(line, "cart-line", (next) => setCartQty(line.id, next), () => setCartQty(line.id, 0))
    )
  );
  cartTotal.textContent = inr(linesTotal(lines));
  cartEmpty.hidden = count > 0;
  cartFoot.hidden = count === 0;
  cartCheckout.hidden = count === 0;

  document.querySelectorAll("[data-add]").forEach((button) => {
    const inCart = cart.get(button.dataset.add);
    button.classList.toggle("is-added", Boolean(inCart));
    button.textContent = inCart ? `In cart · ${inCart}` : "Add to cart";
  });
}

function openCart() {
  cartLastFocused = document.activeElement;
  cartModal.hidden = false;
  cartModal.classList.add("is-open");
  document.body.classList.add("checkout-open");
  cartModal.querySelector(".checkout-close").focus();
}

function closeCart(restoreFocus = true) {
  cartModal.classList.remove("is-open");
  cartModal.hidden = true;
  document.body.classList.remove("checkout-open");
  if (restoreFocus && cartLastFocused) cartLastFocused.focus();
}

document.querySelectorAll("[data-add]").forEach((button) => {
  button.addEventListener("click", () => addToCart(button.dataset.add));
});

cartFab.addEventListener("click", openCart);

document.querySelectorAll("[data-cart-close]").forEach((button) => {
  button.addEventListener("click", () => closeCart());
});

cartModal.addEventListener("click", (event) => {
  if (event.target === cartModal) closeCart();
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && cartModal.classList.contains("is-open")) closeCart();
});

cartCheckout.addEventListener("click", () => {
  cartCheckout.disabled = true;
  cartCheckout.textContent = "Opening checkout…";
  // Let any in-flight quantity change land first, or checkout could open on a
  // cart that is one click out of date.
  cartWrites.finally(() => {
    location.href = routeRoot + "checkout";
  });
});

renderCart();

/* ---------- nav links into the cinematic sequence ---------- */

// The story panels are position:absolute inside a position:sticky stage, so
// their document position never moves — a native "#hampers" jump lands at the
// top of the sequence, not on the hamper panel. Each panel is instead revealed
// at a known scroll depth (the segmentInOut windows above), so these links
// scroll to that depth rather than to the element.
const panelDepth = new Map([
  ["cinema", 0],
  ["audio", 1100], // frame2 fully open across 900–1300
  ["hampers", 2340], // frame3 fully open across 2140–2540
]);

for (const link of document.querySelectorAll('.site-nav a[href^="#"], .site-logo[href^="#"]')) {
  const id = (link.getAttribute("href") || "").slice(1);
  // Sections in normal flow (#shop, #about, #founder) scroll natively already.
  if (!panelDepth.has(id)) continue;

  link.addEventListener("click", (event) => {
    if (!section) return;
    event.preventDefault();
    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    const maxDepth = Math.max(0, section.offsetHeight - window.innerHeight);
    window.scrollTo({
      top: sectionTop + Math.min(panelDepth.get(id), maxDepth),
      behavior: reduceMotion.matches ? "auto" : "smooth",
    });
  });
}

/* ---------- phone menu ---------- */

// Below 640px the CSS turns .site-nav into a full-screen sheet; this only flips
// the class it keys off. The button is display:none on desktop, so the listener
// never fires there and the inline nav row is left alone.
const siteHeader = document.querySelector(".site-header");
const navToggle = document.querySelector(".nav-toggle");
const siteNav = document.querySelector(".site-nav");

if (siteHeader && navToggle) {
  const setMenu = (open) => {
    siteHeader.classList.toggle("nav-open", open);
    navToggle.setAttribute("aria-expanded", open ? "true" : "false");
    navToggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
    // The header is absolutely placed inside the sticky stage, so letting the
    // page scroll under an open sheet would carry the close button off-screen.
    document.body.style.overflow = open ? "hidden" : "";
  };

  navToggle.addEventListener("click", () => {
    setMenu(!siteHeader.classList.contains("nav-open"));
  });

  // Every link has to close the sheet by hand: the panel links above call
  // preventDefault and scroll instead of navigating, and even the native
  // #shop / #about jumps would otherwise land behind an opaque overlay.
  // Capture phase, so the scroll lock is lifted before those handlers run —
  // a smooth scroll started while the body is locked goes nowhere.
  if (siteNav) {
    siteNav.addEventListener(
      "click",
      (event) => {
        if (event.target.closest("a")) setMenu(false);
      },
      true
    );
  }

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") setMenu(false);
  });

  // Rotating to landscape past the breakpoint hides the sheet but would leave
  // the body scroll-locked, so drop the open state with the layout.
  const phone = window.matchMedia("(max-width: 640px)");
  phone.addEventListener("change", () => {
    if (!phone.matches) setMenu(false);
  });
}

// The page renders empty-carted, then catches up with the session's real cart.
fetch(routeRoot + "cart.js", { headers: { Accept: "application/json" } })
  .then((response) => response.json())
  .then(syncFromShopify)
  .catch(() => {
    /* first paint already showed an empty cart; nothing better to do */
  });
